import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserInfoApi,
  changePasswordApi,
  checkUsernameValidApi,
  changeUsernameApi,
  changeInvalidPhoneNumberStep1Api,
  changeInvalidPhoneNumberStep2Api,
  normalizePhoneNumber,
  setReferralCodeApi,
} from "../../services/authApi";
import { logoutUser } from "../../utils/handleJWT";
import {
  checkResponseData,
  checkErrorMessage,
} from "../../utils/responseHandler";
import "./UserSettingsModal.css";

const UserSettingsModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  // State برای اطلاعات کاربر
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // State برای نام کاربری
  const [newUsername, setNewUsername] = useState("");
  const [currentUsername, setCurrentUsername] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState("");

  // State برای تغییر رمز عبور
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // State برای ثبت شماره همراه
  const [phoneInput, setPhoneInput] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [sendingCode, setSendingCode] = useState(false);

  // State برای ثبت جیمیل
  const [email, setEmail] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailError, setEmailError] = useState("");

  // State برای کد معرف
  const [referralCode, setReferralCode] = useState("");
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralError, setReferralError] = useState("");
  const [savingReferral, setSavingReferral] = useState(false);

  // لود اطلاعات کاربر هنگام باز شدن مودال
  useEffect(() => {
    if (isOpen) {
      loadUserInfo();
    } else {
      // ریست استیت‌ها هنگام بسته شدن مودال
      resetStates();
    }
  }, [isOpen]);

  const resetStates = () => {
    setShowPhoneInput(false);
    setShowCodeInput(false);
    setShowEmailInput(false);
    setShowReferralInput(false);
    setPhoneInput("");
    setConfirmCode("");
    setEmail("");
    setReferralCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccessMessage("");
    setUsernameAvailable(null);
    setUsernameError("");
  };

  // تابع لود اطلاعات کاربر
  // جایگزینی تابع loadUserInfo با نسخه اصلاح شده

  const loadUserInfo = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getUserInfoApi();

      // بررسی ساختار صحیح پاسخ
      let userData = null;

      if (response?.data?.success && response?.data?.data?.data?.[0]) {
        userData = response.data.data.data[0];
      } else if (response?.success && response?.data?.data?.[0]) {
        userData = response.data.data[0];
      } else {
        // استفاده از checkResponseData
        const result = checkResponseData(response);
        if (result.success && result.data) {
          // اگر result.data آرایه است
          if (Array.isArray(result.data)) {
            userData = result.data[0];
          } else {
            userData = result.data;
          }
        }
      }

      if (userData) {
        setUserInfo(userData);
        setCurrentUsername(userData.userName || "");
        setNewUsername(userData.userName || "");

        // ذخیره در localStorage
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        // اگر اطلاعات در localStorage وجود داشت
        const localUserData = localStorage.getItem("userData");
        if (localUserData) {
          try {
            const parsedData = JSON.parse(localUserData);
            setUserInfo(parsedData);
            setCurrentUsername(parsedData.userName || "");
            setNewUsername(parsedData.userName || "");
          } catch (e) {
            console.error("خطا در پارس اطلاعات کاربر:", e);
          }
        }
      }
    } catch (err) {
      console.error("خطا در دریافت اطلاعات کاربر:", err);
      const errorDetails = checkErrorMessage(err);
      setError(errorDetails.message);

      // تلاش برای دریافت از localStorage
      const localUserData = localStorage.getItem("userData");
      if (localUserData) {
        try {
          const parsedData = JSON.parse(localUserData);
          setUserInfo(parsedData);
          setCurrentUsername(parsedData.userName || "");
          setNewUsername(parsedData.userName || "");
        } catch (e) {
          console.error("خطا در پارس اطلاعات کاربر:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // تشخیص نوع کاربر
  const getUserTypeDisplay = () => {
    if (!userInfo?.type_user) return "کاربر عادی";

    const types = userInfo.type_user;

    if (types.includes("userPremium")) {
      return "کاربر ویژه";
    } else if (types.includes("subtitlesManager")) {
      return "مدیر زیرنویس";
    } else if (types.includes("appAndroidDev")) {
      return "توسعه‌دهنده";
    } else if (types.includes("user")) {
      return "کاربر عادی";
    }

    return "کاربر عادی";
  };

  // دریافت رنگ نوع کاربر
  const getUserTypeColor = () => {
    if (!userInfo?.type_user) return "#95a5a6";

    const types = userInfo.type_user;

    if (types.includes("userPremium")) {
      return "#f39c12"; // طلایی
    } else if (types.includes("subtitlesManager")) {
      return "#3498db"; // آبی
    } else if (types.includes("appAndroidDev")) {
      return "#9b59b6"; // بنفش
    }

    return "#95a5a6"; // خاکستری
  };

  // بررسی در دسترس بودن نام کاربری
  const checkUsernameAvailability = async () => {
    if (!newUsername || newUsername === currentUsername) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    if (newUsername.length < 3) {
      setUsernameError("نام کاربری باید حداقل ۳ کاراکتر باشد");
      setUsernameAvailable(false);
      return;
    }

    setCheckingUsername(true);
    setUsernameError("");

    try {
      const response = await checkUsernameValidApi(newUsername);
      const result = checkResponseData(response);

      if (result.success) {
        setUsernameAvailable(true);
        setUsernameError("");
      } else {
        setUsernameAvailable(false);
        setUsernameError("این نام کاربری در دسترس نیست");
      }
    } catch (err) {
      console.error("خطا در بررسی نام کاربری:", err);
      const errorDetails = checkErrorMessage(err);
      setUsernameError(errorDetails.message);
      setUsernameAvailable(false);
    } finally {
      setCheckingUsername(false);
    }
  };

  // ذخیره نام کاربری جدید
  const handleSaveUsername = async () => {
    if (!usernameAvailable) {
      setUsernameError("لطفاً یک نام کاربری معتبر انتخاب کنید");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await changeUsernameApi(newUsername);
      const result = checkResponseData(response);

      if (result.success) {
        setSuccessMessage("نام کاربری با موفقیت تغییر کرد");
        setCurrentUsername(newUsername);
        setUserInfo((prev) => ({ ...prev, userName: newUsername }));
        setUsernameAvailable(null);

        // به‌روزرسانی localStorage
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        userData.userName = newUsername;
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        setError(result.message || "خطا در تغییر نام کاربری");
      }
    } catch (err) {
      console.error("خطا در تغییر نام کاربری:", err);
      const errorDetails = checkErrorMessage(err);
      setError(errorDetails.message);
    } finally {
      setLoading(false);
    }
  };

  // تغییر رمز عبور
  const handleChangePassword = async () => {
    // اعتبارسنجی
    if (!newPassword || !confirmPassword) {
      setPasswordError("لطفاً هر دو فیلد را پر کنید");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("رمز عبور باید حداقل ۶ کاراکتر باشد");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("رمز عبور و تکرار آن مطابقت ندارند");
      return;
    }

    setLoading(true);
    setPasswordError("");
    setSuccessMessage("");

    try {
      const response = await changePasswordApi(newPassword, confirmPassword);
      const result = checkResponseData(response);

      if (result.success) {
        setSuccessMessage("رمز عبور با موفقیت تغییر کرد");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordError(result.message || "خطا در تغییر رمز عبور");
      }
    } catch (err) {
      console.error("خطا در تغییر رمز عبور:", err);
      const errorDetails = checkErrorMessage(err);
      setPasswordError(errorDetails.message);
    } finally {
      setLoading(false);
    }
  };

  // پردازش و نرمال‌سازی شماره تلفن برای نمایش
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return "";
    const phoneStr = phone.toString();
    if (phoneStr.startsWith("98")) {
      return "+" + phoneStr;
    }
    return "+98" + phoneStr;
  };

  // پردازش شماره تلفن ورودی کاربر
  const processPhoneInput = (input) => {
    // حذف همه کاراکترهای غیر عددی
    let cleaned = input.replace(/\D/g, "");

    // اگر با 0 شروع شده، حذف کن
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }

    // اگر با 98 شروع نشده، اضافه کن
    if (!cleaned.startsWith("98")) {
      cleaned = "98" + cleaned;
    }

    return cleaned;
  };

  // ارسال کد تایید به شماره همراه
  const handleSendPhoneCode = async () => {
    if (!phoneInput || phoneInput.length < 10) {
      setPhoneError("لطفاً شماره همراه معتبر وارد کنید");
      return;
    }

    setSendingCode(true);
    setPhoneError("");

    try {
      const processedPhone = processPhoneInput(phoneInput);
      const response = await changeInvalidPhoneNumberStep1Api(processedPhone);
      const result = checkResponseData(response);

      if (result.success) {
        setShowCodeInput(true);
        setSuccessMessage("کد تایید به شماره همراه ارسال شد");
      } else {
        setPhoneError(result.message || "خطا در ارسال کد");
      }
    } catch (err) {
      console.error("خطا در ارسال کد:", err);
      const errorDetails = checkErrorMessage(err);
      setPhoneError(errorDetails.message);
    } finally {
      setSendingCode(false);
    }
  };

  // تایید کد و ثبت شماره همراه
  const handleVerifyPhoneCode = async () => {
    if (!confirmCode) {
      setPhoneError("لطفاً کد تایید را وارد کنید");
      return;
    }

    setLoading(true);
    setPhoneError("");

    try {
      const processedPhone = processPhoneInput(phoneInput);
      const response = await changeInvalidPhoneNumberStep2Api(
        processedPhone,
        confirmCode,
      );
      const result = checkResponseData(response);

      if (result.success) {
        setSuccessMessage("شماره همراه با موفقیت ثبت شد");
        setShowPhoneInput(false);
        setShowCodeInput(false);
        setPhoneInput("");
        setConfirmCode("");

        // به‌روزرسانی اطلاعات کاربر
        setUserInfo((prev) => ({
          ...prev,
          phoneNumber: parseInt(processedPhone),
        }));

        // به‌روزرسانی localStorage
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        userData.phoneNumber = parseInt(processedPhone);
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        setPhoneError(result.message || "کد تایید اشتباه است");
      }
    } catch (err) {
      console.error("خطا در تایید کد:", err);
      const errorDetails = checkErrorMessage(err);
      setPhoneError(errorDetails.message);
    } finally {
      setLoading(false);
    }
  };

  // ثبت کد معرف
  const handleSaveReferralCode = async () => {
    if (!referralCode || referralCode.length < 3) {
      setReferralError("لطفاً کد معرف معتبر وارد کنید");
      return;
    }

    setSavingReferral(true);
    setReferralError("");
    setSuccessMessage("");

    try {
      const response = await setReferralCodeApi(referralCode);
      const result = checkResponseData(response);

      if (result.success) {
        setSuccessMessage("کد معرف با موفقیت ثبت شد");
        setShowReferralInput(false);
        setReferralCode("");

        // به‌روزرسانی اطلاعات کاربر
        setUserInfo((prev) => ({ ...prev, presenterCode: referralCode }));

        // به‌روزرسانی localStorage
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        userData.presenterCode = referralCode;
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        setReferralError(result.message || "خطا در ثبت کد معرف");
      }
    } catch (err) {
      console.error("خطا در ثبت کد معرف:", err);
      const errorDetails = checkErrorMessage(err);
      setReferralError(errorDetails.message);
    } finally {
      setSavingReferral(false);
    }
  };

  // خروج از حساب کاربری
  const handleLogout = () => {
    if (window.confirm("آیا از خروج از حساب کاربری خود مطمئن هستید؟")) {
      logoutUser("خروج توسط کاربر");
      window.location.href = "/";
    }
  };

  // بررسی اینکه شماره همراه نامعتبر است
  const isPhoneNumberInvalid = () => {
    if (!userInfo?.phoneNumber) return true;
    const phoneStr = userInfo.phoneNumber.toString();
    return phoneStr.includes("98000") || phoneStr === "98000XXXXXX";
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>تنظیمات کاربری</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          {loading && !userInfo && (
            <div className="inline-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              <span>در حال بارگذاری اطلاعات...</span>
            </div>
          )}

          {!loading && userInfo ? (
            <>
              {/* بخش اطلاعات کاربر */}
              <div className="user-info-header">
                <div className="user-avatar">
                  <i className="fas fa-user-circle"></i>
                </div>
                <div className="user-details">
                  <h3>{currentUsername || "کاربر"}</h3>
                  <span
                    className="user-type-badge"
                    style={{ backgroundColor: getUserTypeColor() }}
                  >
                    {getUserTypeDisplay()}
                  </span>
                </div>
              </div>

              {/* بخش نام کاربری */}
              <div className="settings-section">
                <h3>نام کاربری</h3>
                <div className="current-value">
                  <span className="label">نام کاربری فعلی:</span>
                  <span className="value">
                    {userInfo?.userName || currentUsername || "تعیین نشده"}
                  </span>
                </div>

                <div className="input-group">
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => {
                      setNewUsername(e.target.value);
                      setUsernameAvailable(null);
                      setUsernameError("");
                    }}
                    onBlur={checkUsernameAvailability}
                    placeholder="نام کاربری جدید"
                    disabled={loading}
                  />
                  <button
                    className="check-btn"
                    onClick={checkUsernameAvailability}
                    disabled={
                      checkingUsername ||
                      !newUsername ||
                      newUsername === (userInfo?.userName || currentUsername)
                    }
                  >
                    {checkingUsername ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      "بررسی"
                    )}
                  </button>
                </div>

                {usernameAvailable === true && (
                  <div className="success-text">
                    <i className="fas fa-check-circle"></i> نام کاربری در دسترس
                    است
                  </div>
                )}

                {usernameError && (
                  <div className="error-text">{usernameError}</div>
                )}

                {usernameAvailable && (
                  <button
                    className="save-btn"
                    onClick={handleSaveUsername}
                    disabled={loading}
                  >
                    ذخیره نام کاربری
                  </button>
                )}
              </div>

              {/* بخش شماره همراه */}
              <div className="settings-section">
                <h3>شماره همراه</h3>

                {isPhoneNumberInvalid() ? (
                  <div className="info-box">
                    <p className="warning-text">
                      <i className="fas fa-exclamation-triangle"></i>
                      هیچ شماره تلفنی برای این اکانت ثبت نشده
                    </p>

                    {!showPhoneInput ? (
                      <button
                        className="primary-btn"
                        onClick={() => setShowPhoneInput(true)}
                      >
                        ثبت شماره همراه
                      </button>
                    ) : (
                      <div className="phone-input-section">
                        <div className="phone-input-wrapper">
                          <span className="country-code">+98</span>
                          <input
                            type="tel"
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(e.target.value)}
                            placeholder="مثال: 9123456789"
                            disabled={showCodeInput || sendingCode}
                            className="phone-number-input"
                          />
                        </div>

                        {!showCodeInput && (
                          <button
                            className="send-code-btn"
                            onClick={handleSendPhoneCode}
                            disabled={sendingCode || !phoneInput}
                          >
                            {sendingCode ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              "ارسال کد"
                            )}
                          </button>
                        )}

                        {showCodeInput && (
                          <div className="code-input-section">
                            <input
                              type="text"
                              value={confirmCode}
                              onChange={(e) => setConfirmCode(e.target.value)}
                              placeholder="کد تایید ۴ رقمی"
                              maxLength="4"
                            />
                            <button
                              className="verify-btn"
                              onClick={handleVerifyPhoneCode}
                              disabled={loading}
                            >
                              تایید کد
                            </button>
                          </div>
                        )}

                        {phoneError && (
                          <div className="error-text">{phoneError}</div>
                        )}
                      </div>
                    )}

                    <p className="help-text">
                      <i className="fas fa-info-circle"></i>
                      با ثبت کردن شماره همراه می‌تونین از فراموشی کلمه عبور یا
                      ورود با شماره همراه استفاده کنید
                    </p>
                  </div>
                ) : (
                  <div className="info-box success-box">
                    <p className="success-text">
                      <i className="fas fa-phone"></i>
                      {formatPhoneForDisplay(userInfo?.phoneNumber)}
                    </p>
                  </div>
                )}
              </div>

              {/* بخش جیمیل */}
              <div className="settings-section">
                <h3>جیمیل</h3>

                {!userInfo?.email ? (
                  <div className="info-box">
                    <p className="warning-text">
                      <i className="fas fa-exclamation-triangle"></i>
                      جیمیلی ثبت نشده است
                    </p>

                    {!showEmailInput ? (
                      <button
                        className="primary-btn"
                        onClick={() => setShowEmailInput(true)}
                      >
                        ثبت جیمیل
                      </button>
                    ) : (
                      <div className="email-input-section">
                        <div className="input-group">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@gmail.com"
                          />
                          <button
                            className="save-btn"
                            onClick={() => {
                              // این بخش نیاز به API دارد
                              setSuccessMessage(
                                "این قابلیت به زودی اضافه می‌شود",
                              );
                            }}
                          >
                            ذخیره
                          </button>
                        </div>
                        {emailError && (
                          <div className="error-text">{emailError}</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="info-box success-box">
                    <p className="success-text">
                      <i className="fas fa-envelope"></i>
                      {userInfo.email}
                    </p>
                  </div>
                )}
              </div>

              {/* بخش کد معرف */}
              <div className="settings-section">
                <h3>کد معرف</h3>

                {!userInfo?.presenterCode ? (
                  <div className="info-box">
                    <p className="info-text">
                      <i className="fas fa-gift"></i>
                      کد معرفی ثبت نشده است
                    </p>

                    {!showReferralInput ? (
                      <button
                        className="primary-btn"
                        onClick={() => setShowReferralInput(true)}
                      >
                        ثبت کد معرف
                      </button>
                    ) : (
                      <div className="referral-input-section">
                        <div className="input-group">
                          <input
                            type="text"
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            placeholder="کد معرف را وارد کنید"
                          />
                          <button
                            className="save-btn"
                            onClick={handleSaveReferralCode}
                            disabled={savingReferral}
                          >
                            {savingReferral ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              "ثبت"
                            )}
                          </button>
                        </div>
                        {referralError && (
                          <div className="error-text">{referralError}</div>
                        )}
                      </div>
                    )}

                    <p className="help-text">
                      <i className="fas fa-info-circle"></i>
                      با ثبت کد معرف می‌تونین از مزایای ویژه برخوردار شوید
                    </p>
                  </div>
                ) : (
                  <div className="info-box success-box">
                    <p className="success-text">
                      <i className="fas fa-ticket-alt"></i>
                      کد معرف: {userInfo.presenterCode}
                    </p>
                  </div>
                )}
              </div>

              {/* بخش تغییر رمز عبور */}
              <div className="settings-section">
                <h3>تغییر کلمه عبور</h3>
                <div className="password-section">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="کلمه عبور جدید"
                    disabled={loading}
                  />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="تکرار کلمه عبور جدید"
                    disabled={loading}
                  />

                  {passwordError && (
                    <div className="error-text">{passwordError}</div>
                  )}

                  <button
                    className="save-btn"
                    onClick={handleChangePassword}
                    disabled={loading || !newPassword || !confirmPassword}
                  >
                    {loading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      "ذخیره کلمه عبور"
                    )}
                  </button>
                </div>
              </div>

              {/* دکمه حذف حساب */}
              <div className="settings-section delete-account-section">
                <button
                  className="delete-account-btn"
                  onClick={() => {
                    onClose();
                    navigate("/deleteaccount");
                  }}
                >
                  <i className="fas fa-trash-alt"></i>
                  حذف حساب کاربری
                </button>
              </div>

              {/* دکمه خروج */}
              <div className="settings-section logout-section">
                <button className="logout-btn" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i>
                  خروج از حساب کاربری
                </button>
              </div>
            </>
          ) : !loading && !userInfo ? (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              خطا در دریافت اطلاعات کاربری
              <button className="retry-btn" onClick={loadUserInfo}>
                تلاش مجدد
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UserSettingsModal;
